export const AUDIT_USER_TYPES = Object.freeze([
  { value: 'all', label: '전체 사용자 유형' },
  { value: 'admin', label: '운영자' },
  { value: 'realtor', label: '공인중개사' },
  { value: 'partner', label: '제휴업체' },
  { value: 'user', label: '일반 사용자' },
  { value: 'guest', label: '비로그인 사용자' },
  { value: 'system', label: '시스템' },
]);

const USER_TYPE_LABELS = Object.freeze(
  AUDIT_USER_TYPES.reduce((labels, item) => ({ ...labels, [item.value]: item.label }), {}),
);

const normalizeCode = (value) => String(value || '').trim().toLowerCase();
const hasActorIdentity = (actor = {}) => Boolean(
  actor.uid || actor.email || actor.displayName || actor.realtorId || actor.officeName,
);

export const resolveAuditUserType = ({ role, actor = {}, action = '', source = '' } = {}) => {
  const code = normalizeCode(role || actor.role || actor.userType);
  const sourceCode = normalizeCode(source);
  const actionCode = normalizeCode(action);

  if (/^(system|worker|service|service-account|batch|scheduler|cron|api)$/.test(code)) return 'system';
  if (/(^|[-_.])(admin|administrator|operator|staff|manager|master)([-_.]|$)/.test(code)) return 'admin';
  if (/(^|[-_.])(realtor|broker|agent)([-_.]|$)/.test(code)) return 'realtor';
  if (/(^|[-_.])(partner|vendor|affiliate|provider|agency)([-_.]|$)/.test(code)) return 'partner';
  if (/(^|[-_.])(user|member|customer|client|tenant|lessor|lessee|landlord)([-_.]|$)/.test(code)) return 'user';
  if (/^(anonymous|guest|unauthenticated|public)$/.test(code)) {
    if (!hasActorIdentity(actor) && (/^(worker|system|api)$/.test(sourceCode) || actionCode.startsWith('api.'))) {
      return 'system';
    }
    return 'guest';
  }
  if (hasActorIdentity(actor)) return 'user';
  if (actionCode.startsWith('analytics.')) return 'guest';
  return /^(worker|system|api)$/.test(sourceCode) ? 'system' : 'guest';
};

export const getAuditUserTypeLabel = (value) => USER_TYPE_LABELS[value] || '일반 사용자';

const TARGET_TYPE_LABELS = Object.freeze({
  a: '링크',
  api_route: 'API 경로',
  archive: '보관본',
  automation_job: '자동화 작업',
  automation_job_preflight: '자동화 사전 점검',
  button: '버튼',
  common_attachment: '공통 첨부파일',
  contract: '계약',
  contract_document: '계약 문서',
  contract_documents: '계약 문서',
  contract_room: '계약방',
  document: '문서',
  document_text_layer: '문서 편집 내용',
  ieum_web: '이음 운영 웹',
  page: '페이지',
  registry_candidate: '등기 대상 후보',
  route: '화면',
  section: '화면 영역',
  send_manifest: '발송 목록',
  signature: '서명',
  stored_file: '저장 파일',
  user: '사용자',
  user_consent: '사용자 동의',
  webview_message: '앱 통신',
});

export const getAuditTargetTypeLabel = (value) => {
  const key = normalizeCode(value);
  return TARGET_TYPE_LABELS[key] || '기타 대상';
};

const ROUTE_LABELS = Object.freeze({
  '/': '홈',
  '/login': '로그인',
  '/signup': '회원가입',
    '/admin': '관리자 대시보드',
    '/admin/mail': '관리자 메일',
    '/admin/mail/compose': '메일 발송',
    '/admin/mail/inbox': '메일 받은편지함',
    '/admin/mail/shared': '공용메일함',
    '/admin/mail/sent': '메일 발신내역',
    '/admin/mail/drafts': '메일 임시저장함',
    '/admin/mail/archive': '메일 보관함',
    '/admin/mail/spam': '스팸메일함',
    '/admin/mail/trash': '메일 휴지통',
    '/admin/mail/settings': '메일 설정',
  '/admin/service/requests': '전체 서비스 요청',
  '/admin/settings/accounts': '관리자 계정',
  '/admin/settings/logs': '감사 로그',
  '/admin/settings/service': '서비스 설정',
  '/admin/settings/pricing': 'DB 가격·수수료 설정',
  '/admin/settings/formmail': '폼메일 설정',
  '/admin/settings/terms': '약관 설정',
  '/admin/settings/consents': '동의 이력 관리',
  '/admin/settings/app-update': '앱 업데이트',
  '/admin/realtorauto/dashboard': '부동산 오토 대시보드',
  '/admin/realtorauto/irospdf': '등기부 PDF 관리',
  '/admin/realtorauto/crawler': '검색·수집 관리',
  '/admin/realtorauto/worker-queue': '자동화 작업 대기열',
  '/admin/realtorauto/failure-log': '오류 로그',
  '/admin/realtorauto/payments': '결제 내역',
  '/admin/realtorauto/payment-macro': '결제 자동화',
  '/admin/realtorauto/template': '문서 템플릿 관리',
  '/realtorauto': '부동산 오토',
  '/realtorauto/': '부동산 오토',
});

const ROUTE_SEGMENT_LABELS = Object.freeze({
  admin: '관리자',
  realtorauto: '부동산 오토',
  dashboard: '대시보드',
  requests: '서비스 요청',
  settings: '설정',
  logs: '감사 로그',
  payments: '결제 내역',
  crawler: '검색·수집',
  template: '문서 템플릿',
});

export const getAuditRouteLabel = (value) => {
  const raw = String(value || '').trim();
  const path = raw.split('?')[0].replace(/\/+$/, '') || '/';
  if (ROUTE_LABELS[path]) return ROUTE_LABELS[path];
  const labels = path
    .split('/')
    .filter(Boolean)
    .map((part) => ROUTE_SEGMENT_LABELS[normalizeCode(part)])
    .filter(Boolean);
  return labels.length > 0 ? labels.join(' > ') : '해당 페이지';
};

const ACTION_LABELS = Object.freeze({
  'auth.session.start': '로그인 세션 시작',
  'auth.logout': '로그아웃',
  'consent.record': '약관 및 개인정보 동의 기록',
  'file.download.denied': '파일 다운로드 차단',
  'file.head': '파일 정보 확인',
  'file.download': '파일 다운로드',
  'admin.payment.refund': '결제 환불 처리',
  'admin.settings.update': '관리자 설정 변경',
  'admin.cashgate.balance.check': '결제 잔액 확인',
  'common_attachment.upload': '공통 첨부파일 업로드',
  'common_attachment.rename': '공통 첨부파일 이름 변경',
  'common_attachment.delete': '공통 첨부파일 삭제',
  'address_source.prefetch': '주소 원천정보 사전 조회',
  'contract.create': '계약방 생성',
  'contract.update': '계약방 수정',
  'contract.delete': '계약방 삭제',
  'registry_candidate.select': '등기 대상 후보 선택',
  'registry_candidate.generate': '등기 대상 후보 생성',
  'document.issue_test': '문서 발급 테스트',
  'document.registry_pdf.upload': '등기부 PDF 업로드',
  'document.registry_source.set': '등기 원천자료 저장',
  'document.registry_pdf.cache_reuse': '기존 등기부 PDF 재사용',
  'document.official_bundle.source_ready': '공부서류 묶음 원천 준비 완료',
  'document.registry_pdf.issue': '등기부 PDF 발급',
  'document.official.source_ready': '공부서류 원천 준비 완료',
  'document.official.create': '공부서류 생성',
  'contract_documents.viewer_ready': '계약 문서 뷰어 준비 완료',
  'contract.share_link.create': '계약방 공유 링크 생성',
  'automation_fields.source_prefetch': '자동입력 원천값 사전 조회',
  'automation_fields.refresh': '자동입력값 최신화',
  'automation_run.preflight_missing_values': '자동화 사전 점검: 원천값 없음',
  'automation_run.preflight_needs_review': '자동화 사전 점검: 직접 확인 필요',
  'automation_run.start': '자동화 실행 시작',
  'send_manifest.create': '발송 목록 생성',
  'text_layer.save': '문서 편집 내용 저장',
  'text_layer.restore': '문서 편집 내용 복원',
  'archive.create': '문서 보관본 생성',
  'signature.create': '서명 생성',
  'api.error': 'API 오류 발생',
});

const WEBVIEW_MESSAGES = Object.freeze({
  PUSH_TOKEN: '앱 푸시 토큰',
  FCM_TOKEN: '앱 알림 토큰',
  BACK_REQUEST: '앱 뒤로가기 요청',
  APP_EXIT_REQUEST: '앱 종료 요청',
  EXIT_APP: '앱 종료 명령',
  WEB_READY: '웹 화면 준비 완료',
});

const ACTION_CONTEXT_LABELS = Object.freeze({
  admin: '관리자',
  address: '주소',
  archive: '문서 보관본',
  automation: '자동화',
  automation_fields: '자동입력값',
  automation_run: '자동화 실행',
  common_attachment: '공통 첨부파일',
  contract: '계약방',
  contract_documents: '계약 문서',
  document: '문서',
  file: '파일',
  registry_candidate: '등기 대상 후보',
  send_manifest: '발송 목록',
  signature: '서명',
  text_layer: '문서 편집 내용',
});

const ACTION_VERB_LABELS = Object.freeze({
  approve: '승인',
  check: '확인',
  create: '생성',
  delete: '삭제',
  denied: '차단',
  download: '다운로드',
  generate: '생성',
  issue: '발급',
  prefetch: '사전 조회',
  refresh: '최신화',
  reject: '거절',
  rename: '이름 변경',
  restore: '복원',
  save: '저장',
  select: '선택',
  set: '저장',
  start: '실행 시작',
  update: '수정',
  upload: '업로드',
});

const hasKorean = (value) => /[가-힣]/.test(String(value || ''));

export const getAuditActionLabel = ({
  action,
  targetType,
  targetId,
  summary,
} = {}) => {
  const actionCode = normalizeCode(action);
  const targetText = String(targetId || '').trim();

  if (actionCode === 'route.view' || actionCode === 'analytics.page_view') {
    return `${getAuditRouteLabel(targetText)} 화면 조회`;
  }
  if (actionCode === 'analytics.click') {
    const subject = targetText || getAuditTargetTypeLabel(targetType);
    return `${subject}${normalizeCode(targetType) === 'button' ? ' 버튼' : ''} 클릭`;
  }
  if (actionCode.startsWith('webview.')) {
    const [, direction = '', messageCode = ''] = String(action || '').split('.');
    const message = WEBVIEW_MESSAGES[messageCode] || '앱 통신 메시지';
    return direction === 'outbound' ? `${message} 전송` : `${message} 수신`;
  }
  if (ACTION_LABELS[actionCode]) return ACTION_LABELS[actionCode];
  if (hasKorean(summary) && normalizeCode(summary) !== actionCode) return String(summary).trim();

  const parts = actionCode.split('.').filter(Boolean);
  const verb = ACTION_VERB_LABELS[parts[parts.length - 1]];
  const contextKey = parts.slice(0, -1).join('_');
  const context = ACTION_CONTEXT_LABELS[contextKey] || ACTION_CONTEXT_LABELS[parts[0]];
  if (context && verb) return `${context} ${verb}`;
  if (verb) return `${getAuditTargetTypeLabel(targetType)} ${verb}`;
  return '기타 시스템 작업';
};

export const getAuditActionGroup = (action) => {
  const code = normalizeCode(action);
  if (/login|signin|sign_in|auth|session|token|password|로그인|인증/.test(code)) return 'auth';
  if (/consent|terms|agreement|동의|약관/.test(code)) return 'consent';
  if (/payment|settlement|mileage|withdraw|receivable|결제|정산|마일리지|출금|미수/.test(code)) return 'payment';
  if (/webview|bridge|fcm|push|deeplink|returnurl|app/.test(code)) return 'webview';
  if (/create|update|delete|approve|reject|assign|status|insert|upsert|생성|수정|삭제|승인|거절|배정|상태/.test(code)) return 'write';
  return 'other';
};
